import { useCallback, useEffect, useRef, useState } from 'react';
import { PrintArea, ViewMap } from '@/types';
import { useDimension } from '@/hooks/useDimension';

const getScaleFactor = (svgElement: SVGSVGElement | null) => {
  if (!svgElement) {
    return 0;
  }

  const visibleArea = svgElement.getElementById('visible-area');
  const viewBoxWidth = svgElement.width.baseVal.value;
  const viewBoxHeight = svgElement.height.baseVal.value;
  const rects = visibleArea.getClientRects();
  const { width, height } = rects.length ? rects[0] : visibleArea.getBoundingClientRect();

  // DEV-222292 prevent scaleX/Y values from being Infinity if width is 0
  const scaleX = viewBoxWidth / (width < 1 ? 1 : width);
  const scaleY = viewBoxHeight / (height < 1 ? 1 : height);

  return Math.min(scaleX, scaleY);
};

export const useScaleFactor = (viewMapOrPrintArea: ViewMap | PrintArea) => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [setMeasurer, { width, height }] = useDimension();
  const setRef = useCallback(
    (element: SVGElement | null) => {
      const svg = element?.ownerSVGElement;
      setMeasurer(svg?.parentElement || null);
      svgRef.current = svg || null;
    },
    [setMeasurer]
  );

  const [scaleFactor, setScaleFactor] = useState<number>(0);
  const maxScaleFactor = 22;
  const calculateScaleFactor = useCallback(() => {
    const newScaleFactor = getScaleFactor(svgRef.current);
    // DEV-222292 Firefox & Chrome calculate a different scaleFactor for the same product types
    const minFFScaleFactor = newScaleFactor > 70 && newScaleFactor < 350;
    const minChromeScaleFactor = newScaleFactor < 0.6;
    if (minFFScaleFactor || minChromeScaleFactor) {
      setScaleFactor(6); // small print area products (ie: mugs, hats, magnets)
    } else {
      setScaleFactor(maxScaleFactor);
    }
  }, [setScaleFactor]);
  useEffect(calculateScaleFactor, [calculateScaleFactor, viewMapOrPrintArea, width, height]);

  return [setRef, scaleFactor, svgRef] as const;
};
